import {
  Component,
  createContext,
  useContext,
  useEffect,
  useState,
  type ErrorInfo,
  type FormEvent,
  type ReactNode,
} from "react";
import { clearStoredAuth, readStoredAuth, writeStoredAuth, type AuthRole, type RoleAuth } from "../lib/auth.ts";
import { api } from "../lib/api.ts";

const AuthContext = createContext<RoleAuth | null>(null);
type AuthStatus = "bootstrapping" | "checking" | "entry" | "submitting" | "unavailable" | "authenticated";
type AuthState = { status: AuthStatus; auth: RoleAuth | null };

export function AuthGate({ role, children }: { role: AuthRole; children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>(() => {
    const stored = readStoredAuth(role);
    if (stored) return { status: "checking", auth: stored };
    return shouldAttemptDevBootstrap() ? { status: "bootstrapping", auth: null } : { status: "entry", auth: null };
  });
  const [token, setToken] = useState("");

  useEffect(() => {
    if (authState.status !== "bootstrapping") return;
    let cancelled = false;

    void api.getDevAuth(role)
      .then((next) => {
        if (cancelled) return;
        writeStoredAuth(next);
        setAuthState({ status: "authenticated", auth: next });
      })
      .catch(() => {
        if (!cancelled) setAuthState({ status: "entry", auth: null });
      });

    return () => {
      cancelled = true;
    };
  }, [authState.status, role]);

  useEffect(() => {
    if (authState.status !== "checking" || !authState.auth) return;
    let cancelled = false;

    void api.checkAccess(authState.auth)
      .then(() => {
        if (!cancelled) setAuthState({ status: "authenticated", auth: authState.auth });
      })
      .catch(() => {
        clearStoredAuth();
        if (!cancelled) setAuthState({ status: "unavailable", auth: null });
      });

    return () => {
      cancelled = true;
    };
  }, [authState.auth, authState.status]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = { role, token: token.trim() };
    if (!next.token) return;
    setAuthState({ status: "submitting", auth: null });

    try {
      await api.checkAccess(next);
      writeStoredAuth(next);
      setAuthState({ status: "authenticated", auth: next });
      setToken("");
    } catch {
      clearStoredAuth();
      setAuthState({ status: "unavailable", auth: null });
    }
  }

  function returnToEntry() {
    clearStoredAuth();
    setToken("");
    setAuthState({ status: "entry", auth: null });
  }

  function markUnavailable() {
    clearStoredAuth();
    setAuthState({ status: "unavailable", auth: null });
  }

  if (authState.status === "bootstrapping" || authState.status === "checking" || authState.status === "submitting") {
    return (
      <main className="auth-page">
        <div className="auth-panel auth-status" data-testid="auth-checking">
          <h1>COMODO access</h1>
          <p>
            {authState.status === "bootstrapping"
              ? "Opening local dev access..."
              : authState.status === "checking"
                ? "Checking access..."
                : "Opening view..."}
          </p>
        </div>
      </main>
    );
  }

  if (authState.status === "unavailable") {
    return (
      <main className="auth-page">
        <section className="auth-panel auth-status" data-testid="auth-unavailable">
          <h1>Access not available</h1>
          <p>This view is not available from this browser right now.</p>
          <button className="btn primary big" data-testid="auth-retry" type="button" onClick={returnToEntry}>
            Try again
          </button>
        </section>
      </main>
    );
  }

  if (authState.status !== "authenticated" || !authState.auth) {
    return (
      <main className="auth-page">
        <form className="auth-panel" data-testid="auth-gate" onSubmit={submit}>
          <div className={`role-badge ${role === "operator" ? "role-admin" : "role-moderator"}`}>
            {role === "operator" ? "Admin View" : "Moderator View"}
          </div>
          <h1>COMODO access</h1>
          <label>
            Access token
            <input
              data-testid="auth-token"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              type="password"
              autoFocus
              autoComplete="current-password"
            />
          </label>
          <button className="btn primary big" data-testid="auth-submit" type="submit" disabled={!token.trim()}>
            Continue
          </button>
        </form>
      </main>
    );
  }

  const auth = authState.auth;

  return (
    <AuthContext.Provider value={auth}>
      <AuthFailureBoundary onAuthError={markUnavailable} resetKey={`${auth.role}:${auth.token}`}>
        {children}
      </AuthFailureBoundary>
    </AuthContext.Provider>
  );
}

export function useRoleAuth(role: AuthRole): RoleAuth {
  const auth = useContext(AuthContext);
  if (!auth || auth.role !== role) throw new Error(`${role} auth is unavailable`);
  return auth;
}

class AuthFailureBoundary extends Component<
  { children: ReactNode; onAuthError: () => void; resetKey: string },
  { error: Error | null }
> {
  state = { error: null };

  static getDerivedStateFromError(error: unknown): { error: Error } {
    return { error: normalizeError(error) };
  }

  componentDidCatch(error: unknown, _info: ErrorInfo): void {
    const normalized = normalizeError(error);
    if (isAuthError(normalized)) this.props.onAuthError();
  }

  componentDidUpdate(prevProps: { resetKey: string }): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (isAuthError(this.state.error)) return null;
    throw this.state.error;
  }
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error;
  return new Error(String(error));
}

function isAuthError(error: Error): boolean {
  return /\bunauthorized\b/i.test(error.message);
}

function shouldAttemptDevBootstrap(): boolean {
  const host = window.location.hostname.replace(/^::ffff:/, "");
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
}
